// hooks/useCustomDataSources.js

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from 'slices/commonSelectors';

export const keys = {
  customDataSources: 'customDataSources',
};

// Fetch custom data sources
export const useCustomDataSources = () => {
  const group_guid = useSelector(selectGroupGuid);

  return useQuery({
    queryKey: [keys.customDataSources, group_guid],
    queryFn: async () => {
      const response = await axios.get(
        `${config.apiGateway.customDataSources}/`,
        {
          params: { group_guid },
        }
      );
      return response.data;
    },
    onError: (error) => {
      console.error('Error fetching custom data sources:', error);
      toast.error('Failed to fetch custom data sources.');
    },
  });
};

// Create a custom data source
export const useCreateCustomDataSource = () => {
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);

  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(
        `${config.apiGateway.customDataSources}/`,
        {
          ...data,
          group_guid,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([keys.customDataSources]);
    },
    onError: (error) => {
      console.error('Error creating custom data source:', error);
      toast.error('Failed to create custom data source.');
    },
  });
};

// Update a custom data source
export const useUpdateCustomDataSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      const response = await axios.put(
        `${config.apiGateway.customDataSources}/${id}`,
        rest
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([keys.customDataSources]);
    },
    onError: (error) => {
      console.error('Error updating custom data source:', error);
      toast.error('Failed to update custom data source.');
    },
  });
};

// Archive a custom data source
export const useDeleteCustomDataSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(
        `${config.apiGateway.customDataSources}/${id}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([keys.customDataSources]);
    },
    onError: (error) => {
      console.error('Error archiving custom data source:', error);
      toast.error('Failed to archive custom data source.');
    },
  });
};

export const useTestCustomDataSource = () => {
  return useMutation({
    mutationFn: async (dataSource) => {
      try {
        let url = dataSource.url;

        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          return {
            success: false,
            status: 400,
            error: 'URL must include a protocol (http:// or https://)',
            data: null,
          };
        }

        try {
          new URL(url);
        } catch {
          return {
            success: false,
            status: 400,
            error: 'Invalid URL format',
            data: null,
          };
        }

        const response = await axios.post(
          `${config.apiGateway.customDataSources}/resource-test`,
          {
            url,
            key_value_pairs: dataSource.key_value_pairs || {},
            header_key_value_pairs: dataSource.header_key_value_pairs || {},
            method: dataSource.method || 'GET',
          }
        );

        return {
          success: true,
          status: response.status,
          data: response.data,
        };
      } catch (error) {
        return {
          success: false,
          status: error.response?.status || 500,
          error: error.message || 'Unknown error',
          data: error.response?.data || null,
        };
      }
    },
    onError: (error) => {
      console.error('Error testing custom data source:', error);
      toast.error('Failed to test custom data source.');
    },
  });
};

export const useAllowedHeaders = () => {
  const oneHour = 60 * 60 * 1000;
  return useQuery({
    queryKey: [keys.customDataSources, 'allowed-headers'],
    queryFn: async () => {
      const response = await axios.get(
        `${config.apiGateway.customDataSources}/allowed-headers`
      );
      return response.data;
    },
    staleTime: oneHour,
    onError: (error) => {
      console.error('Error fetching allowed headers:', error);
      toast.error('Failed to fetch allowed headers.');
    },
  });
};
